import {PATH_KEYS} from "@/src/modules/routing/utils/constants";

import {
  API_PREFIX_FEATURE_MAP_SORTED,
  FEATURE_ROUTE_REGISTRY_SORTED,
  PLATFORM_FEATURE,
} from "./feature-registry";

function normalizePathname(pathname: string): string {
  const stripped = pathname.replace(/^#/, "");
  if (!stripped || stripped === "/") return "/";
  return stripped.startsWith("/") ? stripped : `/${stripped}`;
}

function normalizeApiPath(url?: string): string {
  if (!url) return "";
  const withoutQuery = url.split("?")[0]?.split("#")[0] ?? url;
  try {
    if (withoutQuery.startsWith("http://") || withoutQuery.startsWith("https://")) {
      return new URL(withoutQuery).pathname.replace(/^\/api\/v\d+\/?/, "");
    }
  } catch {
    // fall through
  }
  return withoutQuery.replace(/^\//, "");
}

export function resolveFeatureFromPathname(pathname: string): string {
  const path = normalizePathname(pathname);

  for (const entry of FEATURE_ROUTE_REGISTRY_SORTED) {
    if (path === entry.pathPrefix || path.startsWith(`${entry.pathPrefix}/`)) {
      return entry.feature;
    }
  }

  const segment = path.split("/").filter(Boolean)[0]?.toLowerCase();
  if (segment && segment in PATH_KEYS) {
    return PATH_KEYS[segment as keyof typeof PATH_KEYS];
  }

  return PLATFORM_FEATURE;
}

export function resolveFeatureFromApiPath(url?: string): string {
  const normalized = normalizeApiPath(url).toLowerCase();
  if (!normalized) return PLATFORM_FEATURE;

  for (const entry of API_PREFIX_FEATURE_MAP_SORTED) {
    if (normalized === entry.prefix || normalized.startsWith(`${entry.prefix}/`)) {
      return entry.feature;
    }
  }

  return PLATFORM_FEATURE;
}

export type ResolveFeatureInput = {
  pathname?: string;
  apiUrl?: string;
  metaFeature?: string;
};

export function resolveFeature(input: ResolveFeatureInput): string {
  if (input.metaFeature) return input.metaFeature;

  if (input.pathname) {
    const fromRoute = resolveFeatureFromPathname(input.pathname);
    if (fromRoute !== PLATFORM_FEATURE) return fromRoute;
  }

  if (input.apiUrl) {
    const fromApi = resolveFeatureFromApiPath(input.apiUrl);
    if (fromApi !== PLATFORM_FEATURE) return fromApi;
  }

  if (input.pathname) {
    return resolveFeatureFromPathname(input.pathname);
  }

  return PLATFORM_FEATURE;
}

export function resolveFeatureFromWindowLocation(): string {
  if (typeof window === "undefined") return PLATFORM_FEATURE;
  const hashPath = window.location.hash.replace(/^#/, "") || "/";
  return resolveFeatureFromPathname(hashPath);
}
